import { useState, useMemo, useEffect } from 'react'
import { useGetAllSubscribersQuery } from '../../redux/services/subscriberService'
import AdminPageLayout from '../../components/admin/AdminPageLayout'
import AdminLoadingState from '../../components/admin/AdminLoadingState'
import AdminErrorState from '../../components/admin/AdminErrorState'
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import type { SubscriberApiResponse } from '../../types'

export default function AllSubscribers() {
  const {
    data: subscribers = [],
    isLoading,
    error
  } = useGetAllSubscribersQuery()

  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Check if subscriber is associated with a center
  const isCenterSubscriber = (subscriber: SubscriberApiResponse): boolean => {
    return (
      subscriber.associatedWithCenter === true || subscriber.centerId != null
    )
  }

  // Helper to safely get center name from subscriber
  const getCenterName = (subscriber: SubscriberApiResponse): string | null => {
    // Check if subscriber has center object from API response
    if (subscriber.center?.centerName) {
      return subscriber.center.centerName
    }
    return null
  }

  // Filter subscribers based on search term
  const filteredSubscribers = useMemo(() => {
    if (!searchTerm) return subscribers

    const lowerSearchTerm = searchTerm.toLowerCase()
    return subscribers.filter((subscriber) => {
      const fullName =
        `${subscriber.firstName} ${subscriber.lastName}`.toLowerCase()
      const email = subscriber.email.toLowerCase()
      const centerName = getCenterName(subscriber)?.toLowerCase() || ''

      return (
        fullName.includes(lowerSearchTerm) ||
        email.includes(lowerSearchTerm) ||
        centerName.includes(lowerSearchTerm)
      )
    })
  }, [subscribers, searchTerm])

  // Pagination calculations
  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedSubscribers = filteredSubscribers.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminLoadingState type="table" />
      </AdminPageLayout>
    )
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminErrorState message="Error loading subscribers" />
      </AdminPageLayout>
    )
  }

  return (
    <AdminPageLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-normal text-[#3C2416]">All Subscribers</h1>
        <p className="mt-2 text-gray-600">
          Manage newsletter subscribers and center associations
        </p>
        <p className="text-gray-600">
          {filteredSubscribers.length} of {subscribers.length} subscribers shown
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or center..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#3C2416]">
            Subscriber List
          </h2>
        </div>

        {filteredSubscribers.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No subscribers found matching your search.'
                : 'No subscribers found.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Associated Center
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {subscriber.firstName} {subscriber.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscriber.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isCenterSubscriber(subscriber)
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {isCenterSubscriber(subscriber)
                          ? 'Center Subscriber'
                          : 'General Subscriber'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        const centerName = getCenterName(subscriber)
                        if (centerName) {
                          return (
                            <div>
                              <p className="font-medium">{centerName}</p>
                              {isCenterSubscriber(subscriber) && (
                                <p className="text-xs text-gray-400">
                                  ID:{' '}
                                  {subscriber.centerId ||
                                    subscriber.center?.id ||
                                    'N/A'}
                                </p>
                              )}
                            </div>
                          )
                        }
                        return <span className="text-gray-400">—</span>
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredSubscribers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to{' '}
              {Math.min(endIndex, filteredSubscribers.length)} of{' '}
              {filteredSubscribers.length} results
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-1">
                {/* Show first page */}
                {currentPage > 2 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      1
                    </button>
                    {currentPage > 3 && (
                      <span className="px-1 text-gray-500">...</span>
                    )}
                  </>
                )}

                {/* Show previous page */}
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {currentPage - 1}
                  </button>
                )}

                {/* Show current page */}
                <button className="px-3 py-1 text-sm rounded-lg bg-[#142841] text-white">
                  {currentPage}
                </button>

                {/* Show next page */}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {currentPage + 1}
                  </button>
                )}

                {/* Show last page */}
                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && (
                      <span className="px-1 text-gray-500">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </AdminPageLayout>
  )
}
